import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import type { DiscoveryApi } from "./core/discoveryAccount";
import { z } from "zod";
import type { EstimateRequest } from "./core/estimator";
import { copy, localeOptions, type Locale } from "./i18n";
import { CustomerPortal } from "./components/CustomerPortal";
import { AdminPortal } from "./components/AdminPortal";
import { AuthPage } from "./components/AuthPage";
import { useAuth } from "./auth/authContext";
import { LanguagePicker } from "./components/LanguagePicker";
import { interiorSamplesManifestSchema, type InteriorSample } from "./core/interiorSamples";
import { appShellCopy } from "./appShellContent";
import { SupportCenter } from "./support/SupportCenter";
import { HomePage } from "./home/HomePage";
import { DiscoveryPage } from "./discovery/DiscoveryPage";
import { pageSeo, publicSeoPaths } from "./core/seo";

const SpaceDraftStudio = lazy(async () => { const module = await import("./components/cad/SpaceDraftStudio"); return { default: module.SpaceDraftStudio }; });

const VirtualEstimateStudio = lazy(() => import("./components/virtual/VirtualEstimateStudio").then(module => ({ default: module.VirtualEstimateStudio })));
const FloorPlanStudio = lazy(async () => {
  const module = await import("./components/FloorPlanStudio");
  return { default: module.FloorPlanStudio };
});

const publicStudiosSchema = z.object({ studios: z.array(z.object({ id: z.uuid(), handle: z.string(), name: z.string(), description: z.string().nullable(), status: z.literal("active") })) });
const defaultInquiryEstimate: EstimateRequest = { roomType: "commercial", scope: "partial", materialGrade: "standard", areaSquareMeters: 80, district: "Bangkok" };

function initialLocale(): Locale {
  const parsed = z.enum(["th", "en", "zh", "ja", "ko"]).safeParse(new URLSearchParams(window.location.search).get("lang"));
  return parsed.success ? parsed.data : "th";
}

export function App(): React.JSX.Element {
  const { session } = useAuth();
  const [locale, setLocale] = useState<Locale>(initialLocale);
  const [studios, setStudios] = useState<z.infer<typeof publicStudiosSchema>["studios"]>([]);
  const [interiorSamples, setInteriorSamples] = useState<readonly InteriorSample[]>([]);
  const [sampleStatus, setSampleStatus] = useState<"loading" | "ready" | "error">("loading");
  const [studioStatus, setStudioStatus] = useState<"loading" | "ready" | "error">("loading");
  const text = copy[locale];
  const shell = appShellCopy[locale];
  const discoveryApi = useCallback<DiscoveryApi>((path,init) => {
    const headers = new Headers(init?.headers);
    if (session !== null) headers.set("authorization",`Bearer ${session.access_token}`);
    return fetch(path,{...init,headers,signal:init?.signal??AbortSignal.timeout(10000)});
  },[session]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    let active = true;
    void fetch("/api/studios").then(async (response) => {
      if (!response.ok) throw new Error("Studios unavailable");
      const parsed = publicStudiosSchema.safeParse(await response.json());
      if (!parsed.success) throw new Error("Studios invalid");
      if (active) { setStudios(parsed.data.studios); setStudioStatus("ready"); }
    }).catch(() => { if (active) setStudioStatus("error"); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    void fetch("/api/interior-samples").then(async (response) => {
      if (!response.ok) throw new Error("Interior sample manifest unavailable");
      const parsed = interiorSamplesManifestSchema.safeParse(await response.json());
      if (!parsed.success) throw new Error("Interior sample manifest invalid");
      if (active) {
        setInteriorSamples(parsed.data.samples);
        setSampleStatus("ready");
      }
    }).catch(() => {
      if (active) setSampleStatus("error");
    });
    return () => { active = false; };
  }, []);

  if (window.location.hostname.startsWith("admin.")) return <AdminPortal />;
  if (window.location.pathname === "/support") return <SupportCenter locale={locale} />;
  if (["/login", "/signup", "/forgot-password", "/update-password", "/auth/callback"].includes(window.location.pathname)) return <AuthPage />;
  if (window.location.pathname === "/virtual-estimate") return <Suspense fallback={<main role="status">Loading…</main>}><VirtualEstimateStudio locale={locale}/></Suspense>;
  if (window.location.pathname === "/space-draft") return <Suspense fallback={<main role="status">{locale === "ko" ? "도면 작업공간 준비 중" : locale === "th" ? "กำลังเตรียมพื้นที่เขียนแบบ" : "Loading drawing workspace"}</main>}><SpaceDraftStudio locale={locale} /></Suspense>;
  if (window.location.pathname === "/design-studio") return <Suspense fallback={<main className="grid min-h-screen place-items-center bg-slate-50 text-sm font-semibold text-slate-600">{locale === "th" ? "กำลังเตรียมพื้นที่ออกแบบ 3D" : locale === "ko" ? "3D 설계 공간을 준비하고 있습니다." : "Preparing the 3D design studio."}</main>}><FloorPlanStudio locale={locale} /></Suspense>;
  if (window.location.pathname === "/app") return <main className="min-h-screen bg-slate-50"><header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/95 backdrop-blur-xl"><div className="mx-auto flex h-[68px] max-w-[1600px] items-center justify-between gap-3 px-4 sm:px-6 lg:px-8"><a className="shrink-0" href={`/?lang=${locale}`} aria-label={shell.home}><img className="w-[136px] sm:w-[160px]" src="/brand/decobaan-logo-roof-lowered.png" alt="Decobaan" /></a><nav className="flex items-center gap-2 text-sm font-semibold text-slate-700" aria-label={text.menuOpen}><a className="hidden rounded-xl px-3 py-2 transition hover:bg-slate-100 sm:inline-flex" href={`/?lang=${locale}`}>{shell.home}</a><a className="hidden rounded-xl px-3 py-2 transition hover:bg-blue-50 hover:text-blue-700 md:inline-flex" href={`/design-studio?lang=${locale}`}>{shell.designStudio}</a><LanguagePicker value={locale} options={localeOptions} onChange={(value) => { setLocale(z.enum(["th", "en", "zh", "ja", "ko"]).parse(value)); }} compact />{session === null ? <a className="whitespace-nowrap rounded-xl bg-slate-950 px-3 py-2.5 text-xs text-white transition hover:bg-blue-700 sm:px-4 sm:text-sm" href={`/login?lang=${locale}&next=/app`}>{text.login}</a> : null}</nav></div></header><CustomerPortal estimate={defaultInquiryEstimate} locale={locale} /></main>;

  if (!publicSeoPaths.includes(window.location.pathname)) {
    const missing = pageSeo(new URL(window.location.href));
    return <main className="mx-auto max-w-3xl p-8"><h1 className="text-2xl font-bold">{missing.heading}</h1><p className="my-5">{missing.description}</p><a className="text-blue-700 underline" href={`/?lang=${locale}`}>Decobaan</a></main>;
  }
  const PublicPage = window.location.pathname === "/services" ? HomePage : DiscoveryPage;
  return <PublicPage key={session?.user.id??"guest"} api={session===null?undefined:discoveryApi} locale={locale} signedIn={session !== null} studios={studios} studioStatus={studioStatus} samples={interiorSamples} sampleStatus={sampleStatus} onLocaleChange={(value) => {
    const nextLocale = z.enum(["th", "en", "zh", "ja", "ko"]).parse(value);
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set("lang", nextLocale);
    window.location.assign(nextUrl.href);
  }} />;
}
